import ProtectedComponent from './protected-component.js';

const ROTATION_KEY = 'home-promo-rotation-index';

const DISMISS_KEYS = {
  'invite-friend': 'add-friend-cta-dismissed',
  'create-group': 'home-promo-dismissed-create-group',
  'add-rss-feed': 'home-promo-dismissed-add-rss-feed'
};

const PROMO_VARIANTS = [
  {
    id: 'invite-friend',
    icon: '🤝',
    text: 'Invite a friend to join Fun Old Times!',
    buttonLabel: 'Add Friend',
    action: 'invite-modal'
  },
  {
    id: 'create-group',
    icon: '🌱',
    text: 'Start a group for your crew!',
    buttonLabel: 'Create Group',
    action: 'navigate-groups-new'
  },
  {
    id: 'add-rss-feed',
    icon: '📡',
    text: 'Own your blog — import it here via RSS',
    buttonLabel: 'Add Feed',
    action: 'navigate-rss-feeds'
  }
];

const VARIANT_ORDER = PROMO_VARIANTS.map((v) => v.id);

/**
 * @component home-promo-banner
 * @css home-promo-banner.css
 * @primitives .btn-cta
 *
 * Rotating home-feed pill CTA: invite friend, create group, add RSS feed.
 */
class HomePromoBanner extends ProtectedComponent {
  constructor() {
    super();
    this.friendCount = 0;
    this.groupCount = 0;
    this.rssFeedCount = 0;
    this.activeVariant = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.handleFriendUpdate = () => this.loadCounts();
    this.handleRouteChange = () => {
      if (this.currentUser) {
        this.load();
      }
    };
    document.addEventListener('friend-request-sent', this.handleFriendUpdate);
    document.addEventListener('friend-request-accepted', this.handleFriendUpdate);
    document.addEventListener('friend-removed', this.handleFriendUpdate);
    document.addEventListener('route-changed', this.handleRouteChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('friend-request-sent', this.handleFriendUpdate);
    document.removeEventListener('friend-request-accepted', this.handleFriendUpdate);
    document.removeEventListener('friend-removed', this.handleFriendUpdate);
    document.removeEventListener('route-changed', this.handleRouteChange);
  }

  isDismissed(variantId) {
    try {
      return localStorage.getItem(DISMISS_KEYS[variantId]) === 'true';
    } catch (_e) {
      return false;
    }
  }

  isEligible(variantId) {
    if (this.isDismissed(variantId)) {
      return false;
    }
    if (variantId === 'create-group' && this.groupCount > 0) {
      return false;
    }
    if (variantId === 'add-rss-feed' && this.rssFeedCount > 0) {
      return false;
    }
    return true;
  }

  getEligibleVariantIds() {
    return VARIANT_ORDER.filter((id) => this.isEligible(id));
  }

  advanceRotationIndex() {
    try {
      const current = parseInt(localStorage.getItem(ROTATION_KEY) || '0', 10);
      localStorage.setItem(ROTATION_KEY, String(current + 1));
      return current;
    } catch (_e) {
      return 0;
    }
  }

  pickVariant() {
    const eligible = this.getEligibleVariantIds();
    if (eligible.length === 0) {
      return null;
    }
    const rotationIdx = this.advanceRotationIndex();
    for (let i = 0; i < VARIANT_ORDER.length; i += 1) {
      const candidate = VARIANT_ORDER[(rotationIdx + i) % VARIANT_ORDER.length];
      if (eligible.includes(candidate)) {
        return PROMO_VARIANTS.find((v) => v.id === candidate) || null;
      }
    }
    return null;
  }

  async loadCounts() {
    if (!this.currentUser) {
      return;
    }

    try {
      const [friendsRes, groupsRes, rssRes] = await Promise.all([
        window.API.request('/friends'),
        window.API.request('/groups'),
        window.API.rss.list()
      ]);
      this.friendCount = friendsRes.counts?.friends || 0;
      this.groupCount = Array.isArray(groupsRes.groups) ? groupsRes.groups.length : 0;
      this.rssFeedCount = Array.isArray(rssRes) ? rssRes.length : 0;
    } catch (error) {
      window.logger?.error('Failed to load home promo banner counts', error);
      this.friendCount = 0;
      this.groupCount = 0;
      this.rssFeedCount = 0;
    }
  }

  async load() {
    if (!this.currentUser) {
      this.clear();
      return;
    }

    if (this._loadInFlight) {
      return this._loadInFlight;
    }

    this._loadInFlight = this._load();
    try {
      await this._loadInFlight;
    } finally {
      this._loadInFlight = null;
    }
  }

  async _load() {
    await this.loadCounts();
    this.activeVariant = this.pickVariant();

    if (!this.activeVariant) {
      this.clear();
      return;
    }

    this.render();
    this.attachEventListeners();
  }

  clear() {
    this.innerHTML = '';
    this.activeVariant = null;
  }

  dismissCurrentVariant() {
    if (!this.activeVariant) {
      return;
    }
    try {
      localStorage.setItem(DISMISS_KEYS[this.activeVariant.id], 'true');
    } catch (_e) {
      // ignore
    }
    this.clear();
  }

  showCloseButton() {
    if (!this.activeVariant) {
      return false;
    }
    if (this.activeVariant.id === 'invite-friend') {
      return this.friendCount >= 2;
    }
    return true;
  }

  render() {
    const variant = this.activeVariant;
    if (!variant) {
      return;
    }

    const showCloseButton = this.showCloseButton();

    this.innerHTML = `
      <section class="home-promo-banner" aria-label="${variant.text}">
        <div class="promo-content">
          <span class="promo-icon" aria-hidden="true">${variant.icon}</span>
          <span class="promo-text">${variant.text}</span>
          <button class="btn-cta" id="promoActionBtn" type="button">
            ${variant.buttonLabel}
          </button>
        </div>
        ${showCloseButton ? `
          <button class="promo-close" id="promoCloseBtn" type="button" aria-label="Close">
            ×
          </button>
        ` : ''}
      </section>
    `;
  }

  attachEventListeners() {
    const actionBtn = this.querySelector('#promoActionBtn');
    if (actionBtn) {
      actionBtn.addEventListener('click', () => this.handleAction());
    }

    const closeBtn = this.querySelector('#promoCloseBtn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.dismissCurrentVariant());
    }
  }

  handleAction() {
    const variant = this.activeVariant;
    if (!variant) {
      return;
    }

    switch (variant.action) {
    case 'invite-modal':
      this.openInviteModal();
      break;
    case 'navigate-groups-new':
      if (window.router?.navigate) {
        window.router.navigate('/groups/new');
      } else {
        window.location.href = '/groups/new';
      }
      break;
    case 'navigate-rss-feeds':
      if (window.router?.navigate) {
        window.router.navigate('/settings/rss-feeds');
      } else {
        window.location.href = '/settings/rss-feeds';
      }
      break;
    default:
      break;
    }
  }

  openInviteModal() {
    let inviteModal = document.querySelector('invite-friend-modal');
    if (!inviteModal) {
      inviteModal = document.createElement('invite-friend-modal');
      document.body.appendChild(inviteModal);
    }
    inviteModal.open();
  }
}

customElements.define('home-promo-banner', HomePromoBanner);

export default HomePromoBanner;
export { PROMO_VARIANTS, VARIANT_ORDER, DISMISS_KEYS, ROTATION_KEY };
